import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useIdleSession from '../hooks/useIdleSession';
import SessionExpiringDialog from '../components/SessionExpiringDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Durées d'inactivité (terrain pharmacie : 30 min de tolérance, 2 min de warning).
const IDLE_MS = 30 * 60 * 1000;
const WARNING_MS = 2 * 60 * 1000;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('dermascan_token'));
  const [loading, setLoading] = useState(true);
  const interceptorRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem('dermascan_token');
    localStorage.removeItem('dermascan_user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  // Setup axios interceptor to handle 401 errors globally
  useEffect(() => {
    // Remove any existing interceptor
    if (interceptorRef.current !== null) {
      axios.interceptors.response.eject(interceptorRef.current);
    }

    // Add new interceptor — robuste face aux 401 "innocents" (RBAC partiel,
    // routes legacy, calls hors auth) qui ne signifient pas que le token est
    // périmé. Stratégie :
    //   1. 401 sur /auth/login ou /auth/register → on laisse passer
    //   2. 401 sur /auth/me → session vraiment morte → logout + toast
    //   3. Tout autre 401 → on revalide via /auth/me dans une bulle ; si
    //      /auth/me répond 200 le token est valide, on ne déconnecte PAS
    //      (mais on log un warning). Sinon → logout standard.
    let sessionToastShown = false;
    let revalidating = false;
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          const isPublicAuthAttempt = url.includes('/auth/login') || url.includes('/auth/register');
          if (isPublicAuthAttempt) {
            return Promise.reject(error);
          }

          const isAuthMe = url.includes('/auth/me');
          const currentToken = localStorage.getItem('dermascan_token');
          if (!currentToken) {
            // Déjà déconnecté, rien à faire de plus
            return Promise.reject(error);
          }

          // Cas 1 : /auth/me a 401 → session morte pour de vrai
          if (isAuthMe) {
            if (!sessionToastShown) {
              sessionToastShown = true;
              toast.error('Session expirée — veuillez vous reconnecter.', { duration: 6000 });
              setTimeout(() => { logout(); sessionToastShown = false; }, 1500);
            }
            return Promise.reject(error);
          }

          // Cas 2 : 401 sur une autre route → on revalide le token via /auth/me
          // une seule fois pour éviter les boucles si plusieurs requêtes 401
          // partent en parallèle.
          if (revalidating) {
            return Promise.reject(error);
          }
          revalidating = true;
          try {
            const meResp = await axios.get(`${API_URL}/auth/me`, {
              headers: { Authorization: `Bearer ${currentToken}` },
              // empêche l'intercepteur de boucler sur le résultat de cette
              // re-vérification (l'interceptor ne se déclenche qu'à l'erreur).
            });
            if (meResp.status === 200) {
              // Token toujours valide → 401 "innocent" (RBAC partiel,
              // route legacy, etc.). On NE déconnecte PAS.
              console.warn(`[Auth] 401 sur ${url} ignoré : /auth/me OK, token valide`);
              return Promise.reject(error);
            }
            // /auth/me n'a pas répondu 200 → on traite comme session morte
            throw new Error('auth_me_failed');
          } catch (_revalidateErr) {
            if (!sessionToastShown) {
              sessionToastShown = true;
              toast.error('Session expirée — veuillez vous reconnecter.', { duration: 6000 });
              setTimeout(() => { logout(); sessionToastShown = false; }, 1500);
            }
            return Promise.reject(error);
          } finally {
            // Petit délai avant de pouvoir re-valider (en cas de plusieurs 401)
            setTimeout(() => { revalidating = false; }, 800);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [logout]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (phone, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { phone, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('dermascan_token', access_token);
    localStorage.setItem('dermascan_user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (phone, password, name, pharmacyCode = null) => {
    const payload = { phone, password, name };
    if (pharmacyCode) {
      payload.pharmacy_code = pharmacyCode;
    }
    const response = await axios.post(`${API_URL}/auth/register`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('dermascan_token', access_token);
    localStorage.setItem('dermascan_user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const isAdmin = user?.is_admin || user?.role === 'admin';
  const isAgent = user?.role === 'agent' || isAdmin;

  // Configuration de session (idle / warning) — lue depuis /api/system/session-config
  // après login, avec fallback aux valeurs locales par défaut (30 min / 2 min).
  const [sessionCfg, setSessionCfg] = useState({ idleMs: IDLE_MS, warningMs: WARNING_MS });
  useEffect(() => {
    if (!token || !user) return;
    let alive = true;
    axios.get(`${API_URL}/system/session-config`).then((r) => {
      if (!alive) return;
      const idle = (r.data?.idle_seconds || 1800) * 1000;
      const warn = (r.data?.warning_seconds || 120) * 1000;
      setSessionCfg({ idleMs: idle, warningMs: warn });
    }).catch(() => {
      // Si l'endpoint n'est pas dispo, on garde les valeurs par défaut.
    });
    return () => { alive = false; };
  }, [token, user]);

  // Inactivité : actif uniquement quand l'utilisateur est connecté.
  // À expiration → logout silencieux avec toast "Session fermée pour inactivité".
  const handleIdleLogout = useCallback(() => {
    toast.info('Session fermée pour inactivité.', { duration: 5000 });
    logout();
  }, [logout]);

  const { warning, secondsLeft, stayConnected } = useIdleSession({
    enabled: !!token && !!user,
    idleMs: sessionCfg.idleMs,
    warningMs: sessionCfg.warningMs,
    onLogout: handleIdleLogout,
  });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, isAdmin, isAgent }}>
      {children}
      <SessionExpiringDialog
        open={warning}
        secondsLeft={secondsLeft}
        onStay={stayConnected}
        onLogout={logout}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
